import React from 'react';
import {
  Box,
  Collapse,
  FormControlLabel,
  Stack,
  Switch,
  TableCell,
  TableRow,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { TRemoveAccess, TShareQuilt, TUserQuilt } from 'api/http-client/profileTypes';
import { useRedirectAndScroll } from 'hooks';

import { SharedDialog } from 'components/ShadedDialog/SaredDialog';
import { AddUsers, InvitedUsers } from 'components/CreateMemorialQuiltComponents/components';
import { DeleteButton, EditButton, ViewButton, InviteButton } from './TableButtons';

import userProfileStyles from '../styles.module.scss';
import styles from './styles.module.scss';
import { DefaultDialog } from '../../../components/DefaultDialog';
import { useTranslates } from 'providers/translates';

export const QuiltsTableRow: React.FC<{
  userEmail: string;
  quilt: TUserQuilt;
  publicUnpublic: (quilt: TUserQuilt) => void;
  publishUnpublish: (quilt: TUserQuilt) => void;
  removeAccess: (body: TRemoveAccess) => void;
  shareQuilt: (body: TShareQuilt) => void;
  deleteQuilt: (id: string) => void;
  rowStyle: string;
}> = ({
  userEmail,
  quilt,
  publicUnpublic,
  publishUnpublish,
  shareQuilt,
  deleteQuilt,
  rowStyle,
}) => {
  const { translates: t } = useTranslates();
  const myQuilts = t?.myQuilts;
  const [collapse, setCollapse] = React.useState(false);
  const [shareDialog, setShareDialog] = React.useState(false);
  const [deleteDialog, setDeleteDialog] = React.useState(false);
  const { redirectAndScroll } = useRedirectAndScroll({});
 
  const matches_900 = useMediaQuery('(min-width: 900px)');
  const matches_600 = useMediaQuery('(max-width: 600px)');

  return (
    <>
      <TableRow className={rowStyle}>
        <TableCell align="left" width={100} className={userProfileStyles.userProfileBodyTableCell}>
          <Box
            sx={{
              width: { xs: 55, md: 80 },
              height: { xs: 55, md: 80 },
            }}
          >
            <img
              className={styles.imgTable}
              src={quilt.finalImage ? quilt.finalImage.full : 'img/general/bg-2.jpg'}
              alt="img"
            />
          </Box>
          {!matches_900 && (
            <Box className={styles.text}>
              <span>{quilt.name}</span>
            </Box>
          )}
        </TableCell>
        {matches_900 && (
          <TableCell
            align="left"
            className={`${styles.text} ${userProfileStyles.userProfileBodyTableCell}`}
          >
            <span>{quilt.name}</span>
          </TableCell>
        )}

        <TableCell style={{ padding: 5 }} align="left" className={styles.viewButton}>
          <ViewButton
            viewButtonClick={() => redirectAndScroll('/quilts/' + quilt.id + '/memorial')}
            isLocked={quilt.locked}
          />
          <EditButton
            editButtonClick={() => redirectAndScroll('/update/' + quilt.id + '?step=1')}
            isLocked={quilt.locked}
          />
          <DeleteButton
            deleteButtonClick={() => setDeleteDialog(!deleteDialog)}
            isLocked={quilt.locked}
          />
          <InviteButton setCollapse={() => setCollapse(!collapse)} isLocked={quilt.locked} />
        </TableCell>

        <TableCell align="left" className={`${userProfileStyles.userProfileBodyTableCell} ${styles.isPublic}`}>
          <span className={styles.text} style={{ ...(quilt.isPublic ? { opacity: 0.5 } : {}) }}>
            {' '}
            {myQuilts?.myQuiltsDisplayTogglePrivate ?? 'Private'}{' '}
          </span>
          <FormControlLabel
            disabled={quilt.locked}
            control={
              <Switch
                checked={quilt.isPublic}
                onChange={() => publicUnpublic(quilt)}
                size={matches_600 ? 'small' : 'medium'}
              />
            }
            label={''}
            className={styles.text}
            sx={{
              margin: 0,
              marginTop: -1,
            }}
          />
          <span className={styles.text} style={{ ...(!quilt.isPublic ? { opacity: 0.5 } : {}) }}>
            {' '}
            {myQuilts?.myQuiltsDisplayTogglePublic ?? 'Public'}{' '}
          </span>
        </TableCell>

        <TableCell align="left" className={`${userProfileStyles.userProfileBodyTableCell} ${styles.isPublished}`}>
          <span className={styles.text} style={{ ...(quilt.isPublished ? { opacity: 0.5 } : {}) }}>
            {' '}
            {myQuilts?.myQuiltsStatusToggleDraft ?? 'Draft'}{' '}
          </span>
          <FormControlLabel
            disabled={quilt.locked}
            control={
              <Switch
                checked={quilt.isPublished}
                onChange={() => publishUnpublish(quilt)}
                size={matches_600 ? 'small' : 'medium'}
              />
            }
            label={''}
            className={styles.text}
            sx={{
              margin: 0,
              marginTop: -1,
            }}
          />
          <span className={styles.text} style={{ ...(!quilt.isPublished ? { opacity: 0.5 } : {}) }}>
            {' '}
            {myQuilts?.myQuiltsStatusTogglePublished ?? 'Published'}{' '}
          </span>
        </TableCell>
       </TableRow>

      <TableRow style={{ visibility: collapse ? 'visible' : 'collapse' }}>
        <TableCell style={{ padding: 0 }} colSpan={4}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CloseIcon fontSize="large" onClick={() => setCollapse(!collapse)} color="action" />
          </Box>
          <Collapse in={collapse} timeout={1000}>
            {quilt?.id ? (
              <Stack spacing={{ xs: 3, sm: 9 }} mb={{ xs: 2, sm: 6 }} mt={1}>
                {(quilt?.contributors.length > 0 || quilt?.notClaimedInvitations.length > 0) && (
                  <InvitedUsers idQuilt={quilt.id} />
                )}
                <AddUsers pageName={`${quilt.name}`} id={quilt.id}></AddUsers>
              </Stack>
            ) : (
              <></>
            )}
          </Collapse>
        </TableCell>
      </TableRow>

      <SharedDialog
        id={quilt.id}
        userEmail={userEmail}
        setShareDialog={setShareDialog}
        shareDialog={shareDialog}
        shareQuilt={shareQuilt}
      />

      <DefaultDialog
        open={deleteDialog}
        setDialog={() => setDeleteDialog(!deleteDialog)}
        title={''}
        text={t?.notifyMessages?.quiltDeleteConfirmation || 'Are you sure you want to delete this quilt?'}
        confirmAction={() => {
          deleteQuilt(quilt.id);
          setDeleteDialog(!deleteDialog);
        }}
      />
    </>
  );
};
